import { Lock } from "lucide-react";
import { FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { AddressFields } from "@/components/forms/AddressFields";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { MaskedSensitiveField } from "./MaskedSensitiveField";
import { CollapsibleSection } from "./CollapsibleSection";
import type {
  AccreditedInvestorBasis,
  AccreditedInvestorStatus,
  AddressDto,
  BankAccountType,
  CounterpartyTaxFilingStatus,
  CreatePersonRequest,
  MaritalStatus,
  PreferredContactMethod,
  PreliminaryCreditScoreSource,
  PrimaryPhoneType,
  USCitizenStatus,
} from "@/types/counterparties";
import { EMPTY_ADDRESS } from "@/types/counterparties";

// PersonForm is the controlled form body shared between Create and Edit.
// State management is intentionally lifted to the host page so the host
// can run dedup-check and critical-field confirmation around the submit.

export type PersonFormState = CreatePersonRequest;

export const EMPTY_PERSON_FORM: PersonFormState = {
  firstName: "",
  middleName: "",
  lastName: "",
  suffix: "",
  dateOfBirth: null,
  ssn: null,
  usCitizen: null,
  maritalStatus: null,
  primaryEmail: "",
  secondaryEmail: "",
  primaryPhone: "",
  primaryPhoneType: null,
  secondaryPhone: "",
  preferredContactMethod: null,
  address: EMPTY_ADDRESS,
  mailingAddress: null,
  industryExperienceYears: null,
  estimatedNetWorth: null,
  estimatedLiquidity: null,
  preliminaryCreditScore: null,
  preliminaryCreditScoreSource: null,
  equityInjectionSource: "",
  accreditedInvestorStatus: null,
  accreditedInvestorBasis: null,
  taxFilingStatus: null,
  originalSaleDate: null,
  originalSaleBasisInfo: "",
  bankACHName: "",
  bankRoutingNumber: null,
  bankAccountNumber: null,
  bankAccountType: null,
  tags: [],
};

interface PersonFormProps {
  value: PersonFormState;
  onChange: (next: PersonFormState) => void;
  /** When editing, the masked SSN/banking come from the server detail. */
  ssnMasked?: string | null;
  bankRoutingMasked?: string | null;
  bankAccountMasked?: string | null;
  /** Whether the user has clicked "Edit" on the SSN field. */
  editingSsn?: boolean;
  onEditSsn?: () => void;
  editingBank?: boolean;
  onEditBank?: () => void;
}

const US_CITIZEN: USCitizenStatus[] = ["Citizen", "PermanentResident", "WorkAuthorization", "Other"];
const MARITAL: MaritalStatus[] = ["Single", "Married", "Divorced", "Widowed", "Other"];
const PHONE_TYPES: PrimaryPhoneType[] = ["Mobile", "Work", "Home", "Other"];
const CONTACT_METHODS: PreferredContactMethod[] = ["Email", "Phone", "Either"];
const CREDIT_SOURCES: PreliminaryCreditScoreSource[] = ["BrokerReported", "SelfReported", "PulledByPhoenix", "Other"];
const ACCRED_STATUS: AccreditedInvestorStatus[] = ["Verified", "SelfDeclared", "NotApplicable", "Unknown"];
const ACCRED_BASIS: AccreditedInvestorBasis[] = ["IncomeTest", "NetWorthTest", "EntityTest", "Professional", "Other"];
const TAX_STATUS: CounterpartyTaxFilingStatus[] = ["ScheduleC", "LLCDisregarded", "LLCPartnership", "SCorp", "CCorp", "Other"];
const BANK_TYPES: BankAccountType[] = ["Checking", "Savings", "Other"];

export function PersonForm({
  value,
  onChange,
  ssnMasked,
  bankRoutingMasked,
  bankAccountMasked,
  editingSsn,
  onEditSsn,
  editingBank,
  onEditBank,
}: PersonFormProps) {
  const set = <K extends keyof PersonFormState>(k: K, v: PersonFormState[K]) =>
    onChange({ ...value, [k]: v });
  const setAddress = (a: AddressDto) => set("address", a);
  const setMailing = (a: AddressDto | null) => set("mailingAddress", a);

  const hasMailing = value.mailingAddress != null;

  // Inline format validation. Required-field "missing" messages are shown only
  // after the user types — the form's submit handler is responsible for the
  // empty-on-submit case so users aren't yelled at before they begin.
  const primaryEmailFormatError = value.primaryEmail
    ? emailFieldError(value.primaryEmail, { required: true })
    : undefined;
  const secondaryEmailFormatError = emailFieldError(value.secondaryEmail, { required: false });
  const primaryPhoneFormatError = value.primaryPhone
    ? phoneFieldError(value.primaryPhone, { required: true })
    : undefined;
  const secondaryPhoneFormatError = phoneFieldError(value.secondaryPhone, { required: false });

  return (
    <div className="flex flex-col gap-space-4">
      <Section title="Identity">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
          <FormField label="First name" required>
            <Input value={value.firstName} onChange={(e) => set("firstName", e.target.value)} />
          </FormField>
          <FormField label="Middle name">
            <Input value={value.middleName ?? ""} onChange={(e) => set("middleName", e.target.value || null)} />
          </FormField>
          <FormField label="Last name" required>
            <Input value={value.lastName} onChange={(e) => set("lastName", e.target.value)} />
          </FormField>
          <FormField label="Suffix">
            <Input value={value.suffix ?? ""} onChange={(e) => set("suffix", e.target.value || null)} />
          </FormField>
          <FormField label="Date of birth">
            <Input
              type="date"
              value={value.dateOfBirth ?? ""}
              onChange={(e) => set("dateOfBirth", e.target.value || null)}
            />
          </FormField>
          <FormField label="SSN" helperText="Stored encrypted; masked everywhere on read.">
            <MaskedSensitiveField
              label="SSN"
              masked={ssnMasked}
              editing={editingSsn || !ssnMasked}
              editingValue={value.ssn ?? ""}
              onChange={(v) => set("ssn", v || null)}
              onRequestEdit={onEditSsn}
              placeholder="123-45-6789"
            />
          </FormField>
          <FormField label="US citizenship">
            <Select
              value={value.usCitizen ?? ""}
              onChange={(e) => set("usCitizen", (e.target.value || null) as USCitizenStatus | null)}
            >
              <option value="">—</option>
              {US_CITIZEN.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="Marital status">
            <Select
              value={value.maritalStatus ?? ""}
              onChange={(e) => set("maritalStatus", (e.target.value || null) as MaritalStatus | null)}
            >
              <option value="">—</option>
              {MARITAL.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
        </div>
      </Section>

      <Section title="Contact">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
          <FormField label="Primary email" required error={primaryEmailFormatError}>
            <Input
              type="email"
              value={value.primaryEmail}
              onChange={(e) => set("primaryEmail", e.target.value)}
              invalid={!!primaryEmailFormatError}
            />
          </FormField>
          <FormField label="Secondary email" error={secondaryEmailFormatError}>
            <Input
              type="email"
              value={value.secondaryEmail ?? ""}
              onChange={(e) => set("secondaryEmail", e.target.value || null)}
              invalid={!!secondaryEmailFormatError}
            />
          </FormField>
          <FormField label="Primary phone" required error={primaryPhoneFormatError}>
            <Input
              type="tel"
              value={value.primaryPhone}
              onChange={(e) => set("primaryPhone", e.target.value)}
              invalid={!!primaryPhoneFormatError}
            />
          </FormField>
          <FormField label="Phone type">
            <Select
              value={value.primaryPhoneType ?? ""}
              onChange={(e) => set("primaryPhoneType", (e.target.value || null) as PrimaryPhoneType | null)}
            >
              <option value="">—</option>
              {PHONE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormField>
          <FormField label="Secondary phone" error={secondaryPhoneFormatError}>
            <Input
              type="tel"
              value={value.secondaryPhone ?? ""}
              onChange={(e) => set("secondaryPhone", e.target.value || null)}
              invalid={!!secondaryPhoneFormatError}
            />
          </FormField>
          <FormField label="Preferred contact">
            <Select
              value={value.preferredContactMethod ?? ""}
              onChange={(e) => set("preferredContactMethod", (e.target.value || null) as PreferredContactMethod | null)}
            >
              <option value="">—</option>
              {CONTACT_METHODS.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </FormField>
        </div>
      </Section>

      <Section title="Address">
        <AddressFields value={value.address} onChange={setAddress} required idPrefix="person-addr" />
        <label className="flex items-center gap-space-2 text-caption mt-space-3">
          <input
            type="checkbox"
            className="h-4 w-4 accent-gold"
            checked={hasMailing}
            onChange={(e) => setMailing(e.target.checked ? { ...value.address } : null)}
          />
          Mailing address differs from physical address
        </label>
        {hasMailing && (
          <div className="mt-space-3">
            <AddressFields
              value={value.mailingAddress ?? EMPTY_ADDRESS}
              onChange={(a) => setMailing(a)}
              required
              idPrefix="person-mail"
            />
          </div>
        )}
      </Section>

      <CollapsibleSection title="Role-specific (Borrower / Seller / Guarantor)">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
          <FormField label="Industry experience (years)">
            <Input
              type="number"
              min={0}
              value={value.industryExperienceYears ?? ""}
              onChange={(e) => set("industryExperienceYears", e.target.value ? Number(e.target.value) : null)}
            />
          </FormField>
          <FormField label="Estimated net worth (USD)">
            <Input
              type="number"
              step="0.01"
              value={value.estimatedNetWorth ?? ""}
              onChange={(e) => set("estimatedNetWorth", e.target.value ? Number(e.target.value) : null)}
            />
          </FormField>
          <FormField label="Estimated liquidity (USD)">
            <Input
              type="number"
              step="0.01"
              value={value.estimatedLiquidity ?? ""}
              onChange={(e) => set("estimatedLiquidity", e.target.value ? Number(e.target.value) : null)}
            />
          </FormField>
          <FormField label="Preliminary credit score">
            <Input
              type="number"
              min={300}
              max={850}
              value={value.preliminaryCreditScore ?? ""}
              onChange={(e) => set("preliminaryCreditScore", e.target.value ? Number(e.target.value) : null)}
            />
          </FormField>
          <FormField label="Credit score source">
            <Select
              value={value.preliminaryCreditScoreSource ?? ""}
              onChange={(e) => set("preliminaryCreditScoreSource", (e.target.value || null) as PreliminaryCreditScoreSource | null)}
            >
              <option value="">—</option>
              {CREDIT_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="Equity injection source" className="md:col-span-3">
            <Input
              value={value.equityInjectionSource ?? ""}
              onChange={(e) => set("equityInjectionSource", e.target.value || null)}
            />
          </FormField>
          <FormField label="Accredited investor status">
            <Select
              value={value.accreditedInvestorStatus ?? ""}
              onChange={(e) => set("accreditedInvestorStatus", (e.target.value || null) as AccreditedInvestorStatus | null)}
            >
              <option value="">—</option>
              {ACCRED_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="Accredited basis">
            <Select
              value={value.accreditedInvestorBasis ?? ""}
              onChange={(e) => set("accreditedInvestorBasis", (e.target.value || null) as AccreditedInvestorBasis | null)}
            >
              <option value="">—</option>
              {ACCRED_BASIS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="Tax filing status">
            <Select
              value={value.taxFilingStatus ?? ""}
              onChange={(e) => set("taxFilingStatus", (e.target.value || null) as CounterpartyTaxFilingStatus | null)}
            >
              <option value="">—</option>
              {TAX_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="Original sale date">
            <Input
              type="date"
              value={value.originalSaleDate ?? ""}
              onChange={(e) => set("originalSaleDate", e.target.value || null)}
            />
          </FormField>
          <FormField label="Original sale basis info" className="md:col-span-3">
            <Input
              value={value.originalSaleBasisInfo ?? ""}
              onChange={(e) => set("originalSaleBasisInfo", e.target.value || null)}
            />
          </FormField>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Banking"
        warning={
          <span className="inline-flex items-center gap-space-1">
            <Lock size={12} strokeWidth={1.5} aria-hidden /> Encrypted at rest. Routing & account numbers are masked everywhere on read.
          </span>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
          <FormField label="ACH name on account" className="md:col-span-2">
            <Input
              value={value.bankACHName ?? ""}
              onChange={(e) => set("bankACHName", e.target.value || null)}
            />
          </FormField>
          <FormField label="Routing #">
            <MaskedSensitiveField
              label="Routing number"
              masked={bankRoutingMasked}
              editing={editingBank || !bankRoutingMasked}
              editingValue={value.bankRoutingNumber ?? ""}
              onChange={(v) => set("bankRoutingNumber", v || null)}
              onRequestEdit={onEditBank}
              placeholder="9-digit routing number"
            />
          </FormField>
          <FormField label="Account #">
            <MaskedSensitiveField
              label="Account number"
              masked={bankAccountMasked}
              editing={editingBank || !bankAccountMasked}
              editingValue={value.bankAccountNumber ?? ""}
              onChange={(v) => set("bankAccountNumber", v || null)}
              onRequestEdit={onEditBank}
              placeholder="Account number"
            />
          </FormField>
          <FormField label="Account type">
            <Select
              value={value.bankAccountType ?? ""}
              onChange={(e) => set("bankAccountType", (e.target.value || null) as BankAccountType | null)}
            >
              <option value="">—</option>
              {BANK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormField>
        </div>
      </CollapsibleSection>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-2 mt-0">
        {title}
      </h3>
      <div className="bg-white-pure rounded-lg border border-border-light p-space-4">
        {children}
      </div>
    </section>
  );
}
